import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, OnDestroy, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import * as L from 'leaflet';
import { LayerGroup, layerGroup } from 'leaflet';
import 'proj4leaflet';
import {
  catchError,
  finalize,
  map,
  Observable,
  of,
  startWith,
  Subscription,
} from 'rxjs';
import { debounceTime, switchMap } from 'rxjs/operators';
import { GEOSERVER_CRS, GeoserverQueryResult } from './feature.model';
import { MapService, wfsUrl } from './map';

const PARCEL_WFS_TYPE_NAME = 'PARCEL_MP_APPROVED';
const SNAP_MAX_SCALE = 2500;

/**
 * GeoMan's snapping only snaps to vector layers (GeoJSON/Polyline/Polygon) on
 * the map. However, parcel boundaries are rendered as WMS.
 *
 * Therefore, when snapping is enabled, fetch parcel boundary geometries via WFS
 * for the current map viewport and add them as hidden/transparent GeoJSON
 * vector layers on the map. GeoMan will automatically snap to these vector
 * layers during drawing.
 *
 * Refresh the snap layer when the map moves.
 * Disable snapping when zoomed out beyond 1:2500.
 *
 * When snapping is disabled, remove the vector layer.
 */
@Injectable({
  providedIn: 'root',
})
export class ParcelSnapService implements OnDestroy {
  private readonly http = inject(HttpClient);
  private readonly mapService = inject(MapService);

  private readonly _snapEnabled = signal(false);
  readonly snapEnabled = computed(() => this._snapEnabled());

  private readonly _isSnapLoading = signal(false);
  readonly isSnapLoading = computed(() => this._isSnapLoading());

  /** True when the map is zoomed out too far for snapping to work. */
  private readonly scaleFactor = toSignal(this.mapService.scaleFactor$);
  readonly isTooZoomedOut = computed(
    () => (this.scaleFactor() ?? Infinity) > SNAP_MAX_SCALE,
  );

  private readonly snapLayerGroup: LayerGroup = layerGroup();
  private snapGeoJsonLayer: L.GeoJSON | null = null;
  private subscription: Subscription | null = null;

  toggleSnap(enabled: boolean) {
    if (this._snapEnabled() === enabled) return;

    this._snapEnabled.set(enabled);

    if (enabled) {
      this.activate();
    } else {
      this.deactivate();
    }
  }

  private activate() {
    this.subscription?.unsubscribe();
    this.subscription = null;

    this.snapLayerGroup.addTo(this.mapService.map);
    this.snapLayerGroup.options = { pmIgnore: false, snapIgnore: false };

    this.subscription = this.mapService.mapMoveEnd$
      .pipe(
        startWith(null),
        debounceTime(300),
        switchMap(() => {
          // If zoomed out too far, clear snap layers and skip fetch
          if (this.isTooZoomedOut()) {
            this.snapLayerGroup.clearLayers();
            return of(null);
          }

          this._isSnapLoading.set(true);
          return this.fetchParcelGeometries().pipe(
            catchError(() => of(this.emptyFeatureCollection())),
            finalize(() => this._isSnapLoading.set(false)),
          );
        }),
      )
      .subscribe((geojson) => {
        if (geojson) this.renderSnapLayer(geojson);
      });
  }

  private deactivate() {
    this._isSnapLoading.set(false);
    this.snapLayerGroup.clearLayers();
    this.snapGeoJsonLayer = null;
    this.mapService.map.removeLayer(this.snapLayerGroup);
    this.subscription?.unsubscribe();
    this.subscription = null;
  }

  private emptyFeatureCollection(): GeoserverQueryResult<GeoJSON.MultiPolygon> {
    return {
      type: 'FeatureCollection',
      features: [],
      crs: GEOSERVER_CRS,
    };
  }

  private fetchParcelGeometries(): Observable<
    GeoserverQueryResult<GeoJSON.MultiPolygon>
  > {
    const bounds = this.mapService.map.getBounds();
    const sw = bounds.getSouthWest();
    const ne = bounds.getNorthEast();
    const bbox = `${sw.lng},${sw.lat},${ne.lng},${ne.lat},EPSG:4326`;

    const params = new URLSearchParams({
      service: 'wfs',
      version: '2.0.0',
      request: 'GetFeature',
      typeName: PARCEL_WFS_TYPE_NAME,
      outputformat: 'json',
      propertyName: 'shape',
      srsName: 'EPSG:4326',
      bbox,
      count: '1024',
    });

    return this.http
      .get<
        GeoserverQueryResult<GeoJSON.MultiPolygon>
      >(`${wfsUrl}?${params.toString()}`)
      .pipe(
        map((result) => ({
          type: 'FeatureCollection' as const,
          features: result.features,
          crs: result.crs,
        })),
      );
  }

  private renderSnapLayer(result: GeoserverQueryResult<GeoJSON.MultiPolygon>) {
    if (this.snapGeoJsonLayer) {
      this.snapLayerGroup.removeLayer(this.snapGeoJsonLayer);
      this.snapGeoJsonLayer = null;
    }

    if (!result.features.length) return;

    this.snapGeoJsonLayer = L.geoJSON(result, {
      style: { weight: 0, fillOpacity: 0, interactive: false },
      snapIgnore: false,
    });

    this.snapLayerGroup.addLayer(this.snapGeoJsonLayer);
  }

  ngOnDestroy(): void {
    this.deactivate();
  }
}
